"use client";

import { useEffect, useState, useRef } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import useAgencyStatus, { AGENCY_STATUS_QUERY_KEY } from "@/app/_module/hooks/useAgencyStatus";
import AgencyCreationStep from "@/app/auth/sign-up/_modules/components/AgencyCreationStep";
import { useQueryClient } from "@tanstack/react-query";
import { getAdminAccess } from "@/app/_module/utils/admin-access";
import { useToast } from "@/app/_module/hooks/use-toast";
import SubscriptionGateModal, { type UserScenario } from "@/app/_module/components/SubscriptionGateModal";

import { useLeadStats } from "../leads/_modules/hooks/queries";

import { AgentPerformance } from "./_modules/components/AgentPerformance";
import { AgentWorkload, GOAL_METRICS } from "./_modules/components/AgentWorkload";
import { AssignmentHistoryComponent } from "./_modules/components/AssignmentHistory";
import { MilestoneHistory } from "./_modules/components/MilestoneHistory";
import { RecentActivity } from "./_modules/components/RecentActivity";
import { AssignedStagesBoxes } from "./_modules/components/AssignedStagesBoxes";
import { SummaryCards } from "./_modules/components/SummaryCards";
import { TabNavigation } from "./_modules/components/TabNavigation";
import { UnassignedLeads } from "./_modules/components/UnassignedLeads";
import { SelectImportListModal } from "./_modules/components/SelectImportListModal";
import {
  TAB_OPTIONS,
} from "./_modules/constants";
import { useUnassignedLeads, useAgentWorkload } from "./_modules/hooks/queries";
import { useStatusTracking } from "./_modules/hooks/useStatusTracking";
import { leadsService } from "../leads/_modules/services/services";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { transformErrorResponse } from "@/app/_module/utils/transform-error-response";
import { endpoint } from "@/app/_module/services/endpoints";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { useLeadAssignmentRealtime } from "./_modules/hooks/useLeadAssignmentRealtime";
import { leadAssignmentService } from "./_modules/services/service";

const LeadAssignment = function () {
  const searchParams = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    // Initialize from URL search params, default to "dashboard"
    return searchParams.get("tab") || "dashboard";
  });
  const queryClient = useQueryClient();
  const router = useRouter();
  const { toast } = useToast();
  const { data: agencyStatus, isLoading: statusLoading, isSuccess: statusSuccess } = useAgencyStatus();

  // Refresh when leads change outside this browser — another owner assigning,
  // a bulk move, an import landing, or a direct database fix. Throttled: a burst
  // of thousands of row events collapses into one refresh. See the hook.
  useLeadAssignmentRealtime(agencyStatus?.agencyId);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);
  const { user } = useUserContext();
  const [hasRunMigration, setHasRunMigration] = useState(false);
  // Desktop: the Lead List modal auto-opens on every visit (owners live in
  // it). On the /m shell that felt naggy — there it opens only on the FIRST
  // visit of the session; after that you reach it via "Select List".
  const [showImportModal, setShowImportModal] = useState(() => {
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/m")) {
      try {
        if (sessionStorage.getItem("m-leadassign-list-shown")) return false;
        sessionStorage.setItem("m-leadassign-list-shown", "1");
      } catch {}
    }
    return true;
  });

  // Re-open modal when navigating to this page
  // Next.js App Router patches pushState for client navigation
  useEffect(() => {
    const origPush = history.pushState.bind(history);
    const origReplace = history.replaceState.bind(history);

    const check = (_: any, __: string, url?: string | URL | null) => {
      const path = url?.toString() || '';
      // User just navigated TO lead-assignment from somewhere else.
      // Shell exception: on /m the modal is first-visit-only per session —
      // re-opening on every navigation read as nagging on a phone.
      if (path.includes('/lead-assignment') && !path.includes('tab=')) {
        if (window.location.pathname.startsWith('/m')) return;
        setTimeout(() => setShowImportModal(true), 100);
      }
    };

    history.pushState = function (...args) {
      origPush(...args);
      check(args[0], args[1] as string, args[2]);
    };
    history.replaceState = function (...args) {
      origReplace(...args);
      check(args[0], args[1] as string, args[2]);
    };

    return () => {
      history.pushState = origPush;
      history.replaceState = origReplace;
    };
  }, []);

  // Access control is handled in the render logic below, not in useEffect
  // This prevents premature redirects before proper checks can run

  // Fetch lead statistics from the actual API
  const { data: statsData } = useLeadStats();

  // Fetch ASSIGNMENT-SPECIFIC status tracking (only assigned leads)
  const { data: statusTrackingData, refetch: refetchStatusTracking } = useStatusTracking();


  // Use the proper hooks for data fetching
  const { data: unassignedData, isLoading: unassignedLoading } = useUnassignedLeads();
  const { data: agentWorkloadData, isLoading: agentLoading } = useAgentWorkload();

  // Derive leads directly from query data (no intermediate state to avoid stale renders)
  const unassignedLeads = unassignedData?.unassignedLeads || [];

  // Function to update agent lead goal
  const handleUpdateLeadGoal = async (
    agentId: string,
    goal: { goalType: string; goalValue: number; goalDuration: string } | null,
    opts?: { silent?: boolean },
  ) => {
    try {
      const agencyId = agencyStatus?.agencyId;
      if (!agencyId) {
        console.error("agencyStatus:", agencyStatus);
        throw new Error("Agency ID not found");
      }

      // null clears the goal; otherwise send the full config.
      const body = goal
        ? { goalType: goal.goalType, goalValue: goal.goalValue, goalDuration: goal.goalDuration }
        : { goalValue: null };

      const response = await api.put({
        url: getApiUrl(`/agencies/${agencyId}/agents/${agentId}/lead-goal`),
        body,
        auth: true,
      });

      if (!response || (response as any).statusCode !== 200) {
        throw new Error("Failed to update goal");
      }

      // Optimistic update for instant feedback (goalCurrent syncs on the next
      // refetch — the backend clears its workload cache on this PUT).
      queryClient.setQueryData(["agentWorkload", agencyId], (old: any) => {
        if (!old?.agents) return old;
        return {
          ...old,
          agents: old.agents.map((a: any) =>
            (a.id === agentId || a.agentId === agentId)
              ? {
                  ...a,
                  goalType: goal?.goalType ?? null,
                  goalValue: goal?.goalValue ?? null,
                  goalDuration: goal?.goalDuration ?? null,
                  leadGoal: goal?.goalType === "lead_count" ? goal.goalValue : null,
                }
              : a
          ),
        };
      });
      // Pull fresh data so the metric's current value populates the bar.
      queryClient.invalidateQueries({ queryKey: ["agentWorkload", agencyId] });

      // Bulk "Apply to All" calls this once per agent and emits its own single
      // summary toast — without this the user got one identical toast per agent.
      if (!opts?.silent) {
        const m = goal ? GOAL_METRICS[goal.goalType] : undefined;
        toast({
          title: "Success",
          description: goal
            // Use the metric's display label, not `goalType.replace("_"," ")` — that
            // rendered "policies submitted" and, for the units that have one, dropped
            // the $ / m entirely.
            ? `Goal set: ${m?.prefix || ""}${goal.goalValue.toLocaleString("en-US")}${
                m?.unit || ""
              } ${m?.short || goal.goalType.replace(/_/g, " ")} (${goal.goalDuration})`
            : "Goal cleared",
        });
      }
    } catch (error) {
      console.error("Error updating goal:", error);
      // A 400 here is the period sanity check ("40,000 dials daily works out
      // to…") — show it verbatim instead of a generic failure.
      const message = transformErrorResponse(error).message || "Failed to update goal. Please try again.";
      toast({ title: "Goal not saved", description: message, variant: "destructive" });
      throw new Error(message);
    }
  };

  // Auto-fix lead assignments if all leads are incorrectly assigned
  useEffect(() => {
    const runAutoMigration = async () => {
      // Only run once per session
      if (hasRunMigration) return;

      // Check if we have the data needed
      if (!unassignedLeads || unassignedLeads.length === 0) return;

      // Check if all leads have assignedToUserId = userId (incorrectly self-assigned)
      const userId = (user as any)?.id || (user as any)?.userId;
      if (!userId) return;

      const selfAssignedLeads = unassignedLeads.filter(
        (lead: any) => lead.assignedToUserId === userId && lead.userId === userId
      );

      // If more than 80% of leads are self-assigned, run migration
      if (selfAssignedLeads.length > 0 && selfAssignedLeads.length >= unassignedLeads.length * 0.8) {
        setHasRunMigration(true);

        try {
          const result = await api.post({
            url: getApiUrl('/api/leads/migration/fix-assignments'),
            auth: true,
          });
          const count = (result as any).data?.updatedCount || 0;

          if (count > 0) {
            // Refresh the data instead of reloading the page
            queryClient.invalidateQueries({ queryKey: ['unassigned-leads'] });
            queryClient.invalidateQueries({ queryKey: ['lead-stats'] });
          }
        } catch (error) {
          setHasRunMigration(false); // Allow retry on next load
        }
      }
    };

    runAutoMigration();
  }, [unassignedLeads, user, hasRunMigration, queryClient]);
  
  // Use agents directly from query data to ensure fresh data after goal updates
  const agents = agentWorkloadData?.agents || [];

  // Listen for agency changes and refetch data
  useEffect(() => {
    const handleAgencyChange = () => {
      // Invalidate all lead assignment queries
      queryClient.invalidateQueries({ queryKey: ['unassignedLeads'] });
      queryClient.invalidateQueries({ queryKey: ['agentWorkload'] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['status-tracking'] });
      // Reload activity data
      void loadActivity();
    };

    window.addEventListener('insuracentral-agency-change', handleAgencyChange);

    return () => {
      window.removeEventListener('insuracentral-agency-change', handleAgencyChange);
    };
  }, [queryClient]);

  // Load real data for Activity + Assignment History
  const [activities, setActivities] = useState<any[]>([]);
  const [assignmentHistory, setAssignmentHistory] = useState<any[]>([]);

  // FAST path: Recent Activity from the cursor feed (~0.4s single SQL pass) so
  // the tab paints immediately; the heavy status-tracking assembly below only
  // gates Assignment History.
  const loadActivityFeed = async () => {
    try {
      const res = await leadAssignmentService.getActivityFeed();
      if (res.items.length > 0) setActivities(res.items);
    } catch { /* keep whatever the slow path delivers */ }
  };

  const loadActivity = async () => {
    try {
      const resp = await leadAssignmentService.getStatusTracking();

      const anyResp = resp as any;

      // Backend returns: { summary, byStatus, activities, assignments }
      // Try multiple paths to extract activities and assignments
      let rawActivities: any[] = [];
      let rawAssignments: any[] = [];

      if (anyResp?.data?.activities) {
        // Nested: resp.data.activities
        rawActivities = anyResp.data.activities;
        rawAssignments = anyResp.data.assignments || [];
      } else if (anyResp?.activities) {
        // Direct: resp.activities
        rawActivities = anyResp.activities;
        rawAssignments = anyResp.assignments || [];
      } else if (Array.isArray(anyResp)) {
        // Response is directly an array (shouldn't happen but handle it)
        rawActivities = anyResp;
        rawAssignments = [];
      }

      const mappedActivities = rawActivities.map((a, idx) => ({
        id: a.id ?? `activity-${idx}`,
        type: (a.type ?? 'activity') as 'lead_assigned' | 'status_changed' | 'call_made' | 'email_sent' | 'activity',
        leadName: a.leadName ?? a.lead?.name ?? a.lead ?? 'Lead',
        agentName: a.agentName ?? a.agent?.name ?? a.agent ?? 'Agent',
        timestamp: a.timestamp ?? a.createdAt ?? new Date().toISOString(),
        description: a.description ?? a.note ?? '',
        householdPrimaryName: a.householdPrimaryName,
        householdPrimaryId: a.householdPrimaryId,
      }));

      const mappedHistory = rawAssignments.map((h, idx) => ({
        id: h.id ?? `assignment-${idx}`,
        leadName: h.leadName ?? h.lead?.name ?? 'Lead',
        agentName: h.agentName ?? h.agent?.name ?? 'Agent',
        agentAgency: h.agentAgency,
        assignedAt: h.assignedAt ?? h.createdAt ?? new Date().toISOString(),
        status: (h.status ?? 'active') as 'active' | 'completed' | 'unassigned',
        notes: h.notes ?? '',
        isHouseholdPrimary: h.isHouseholdPrimary ?? false,
        householdMemberCount: h.householdMemberCount ?? 0,
      }));

      // Sort assignments: active/completed first, then unassigned
      const sortedHistory = mappedHistory.sort((a, b) => {
        const statusOrder: Record<string, number> = { active: 0, completed: 1, unassigned: 2 };
        const aOrder = statusOrder[a.status] ?? 3;
        const bOrder = statusOrder[b.status] ?? 3;

        if (aOrder !== bOrder) return aOrder - bOrder;

        // Within same status, sort by most recent
        return new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime();
      });

      // Fast cursor feed usually wins the race and has deeper data — only use
      // the slow snapshot's activities if the feed produced nothing.
      setActivities((prev) => (prev.length > 0 ? prev : mappedActivities));
      setAssignmentHistory(sortedHistory);
    } catch (error: any) {
      setAssignmentHistory([]);
    }
  };

  useEffect(() => {
    void loadActivityFeed(); // fast — paints Recent Activity right away
    void loadActivity(); // heavy — fills Assignment History when ready
  }, []);

  // Also reload activity when leads are assigned/unassigned (listen for assignment success)
  useEffect(() => {
    const handleAssignmentSuccess = () => {
      void loadActivity();
      // Also refetch status tracking to update status overview
      void refetchStatusTracking();
    };

    window.addEventListener('lead-assigned', handleAssignmentSuccess);
    window.addEventListener('lead-unassigned', handleAssignmentSuccess);
    return () => {
      window.removeEventListener('lead-assigned', handleAssignmentSuccess);
      window.removeEventListener('lead-unassigned', handleAssignmentSuccess);
    };
  }, [refetchStatusTracking]);

  // Build summary stats from dedicated API data (not paginated lead arrays)
  const summaryStats = (() => {
    // Use status tracking summary for accurate assigned/unassigned counts
    // This endpoint returns real-time counts without caching issues
    const trackingSummary = (statusTrackingData as any)?.summary;
    const totalLeads = trackingSummary?.total ?? 0;
    const assignedCount = trackingSummary?.assigned ?? 0;
    const unassignedCount = trackingSummary?.unassigned ?? 0;

    // Active agents count (agents with current workload)
    const activeAgents = Array.isArray(agents) ? agents.filter(a => (a.activeLeadsCount || 0) > 0).length : 0;

    // Total agents (all agents, even if no leads assigned)
    const totalAgents = Array.isArray(agents) ? agents.length : 0;

    // Calculate avg response time from agents
    const agentsWithResponseTime = agents.filter(a => a.avgResponseTime && a.avgResponseTime !== '0m');
    const avgResponseTime = agentsWithResponseTime.length > 0
      ? agentsWithResponseTime[0].avgResponseTime // Use first agent's time as sample
      : "—";

    // Calculate conversion rate from agents
    const totalConversionRate = agents.reduce((sum, a) => sum + (a.conversionRate || 0), 0);
    const conversionRate = totalAgents > 0
      ? `${(totalConversionRate / totalAgents).toFixed(1)}%`
      : "—";

    return {
      totalLeads,
      assignedLeads: assignedCount,
      unassignedLeads: unassignedCount,
      activeAgents,
      totalAgents,
      avgResponseTime,
      conversionRate,
    };
  })();

  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div data-tour="unassigned-leads">
              <UnassignedLeads agents={agents} />
            </div>
            <div data-tour="agent-selector">
              <AgentWorkload agents={agents} isLoading={agentLoading} onUpdateLeadGoal={handleUpdateLeadGoal} />
            </div>
          </div>
        );
      case "status":
        // Top strip = pipeline boxes over ASSIGNED leads across every agent's
        // pipeline — system stages in canonical order + each custom stage that
        // holds leads (the old agency-wide byStatus row read all-zeros once
        // agents got custom pipelines). AgentPerformance below breaks the same
        // numbers down per agent.
        return (
          <div className="space-y-6">
            <AssignedStagesBoxes />
            <AgentPerformance agents={agents} isLoading={agentLoading} />
          </div>
        );
      case "activity":
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            <RecentActivity activities={activities} />
            <AssignmentHistoryComponent assignments={assignmentHistory} />
            <MilestoneHistory />
          </div>
        );
      default:
        return null;
    }
  };

  // Comprehensive access control logic - MUST be before any early returns
  // Use agencyStatus.role as fallback when user context hasn't loaded currentAgency yet
  // ROLE PRECEDENCE FLIP (2026-07-23): agencyStatus.role is authoritative —
  // it's fetched per current agency (keyed query + agency-change invalidation)
  // while user.currentAgency.role is a login-flow snapshot that can lag one
  // agency behind after the login agency-select (showed the "Switch to Your
  // Own Agency" gate on the agency the user OWNS until hard refresh).
  const agencyRole = agencyStatus?.role || user?.currentAgency?.role;
  const userType = user?.user?.userType;
  const accountType = agencyStatus?.accountType;
  // Only actual agency role determines ownership — userType 'agency_owner' is for sub-agency creation only
  const isOwnerOrAdmin = agencyRole === 'owner' || agencyRole === 'agency_owner' || agencyRole === 'admin' || userType === 'admin';
  const isAgent = agencyRole === 'agent' || (userType === 'agent' && !agencyRole);
  const hasAgency = agencyStatus?.hasAgency;
  // Use agencyStatus as fallback for isInAgency to handle race condition on first login
  const isInAgency = !!user?.currentAgency?.agency?.id || (agencyStatus?.hasAgency === true && !!agencyStatus?.agencyId);

  // Determine if user should be blocked from direct URL access
  let shouldBlock = false;

  // Don't block until agency status has successfully loaded — prevents false redirects on first login
  if (!statusSuccess || !agencyStatus) {
    shouldBlock = false;
  }
  // RACE-CONDITION OVERRIDE (narrowed 2026-07-23): only while agencyRole is
  // unloaded — see agency-dashboard/page.tsx for the multi-agency leak this fixes.
  else if (agencyStatus.isOwner && agencyStatus.hasAgency && !agencyRole) {
    shouldBlock = false;
  }
  // RULE 1: Basic users (in or out of agency) need to upgrade
  else if (accountType === 'basic') {
    shouldBlock = true;
  }
  // RULE 2: Independent Full-Fledged/Super Agent users (no agency) need to create a Parent Agency
  else if ((accountType === 'full_fledged' || accountType === 'super_agent') && !isInAgency && !hasAgency) {
    shouldBlock = true;
  }
  // RULE 3: Agents in agencies (Full-Fledged/Super Agent) without sub-agencies need to create a Sub-Agency
  else if ((accountType === 'full_fledged' || accountType === 'super_agent') && isAgent && isInAgency && !hasAgency) {
    shouldBlock = true;
  }
  // RULE 4: Agents (non-owner/admin) cannot access lead assignment — only owners/admins can manage assignments
  // Enterprise owners always have access
  // Only use agencyStatus.isOwner fallback when agencyRole hasn't loaded (race condition)
  // If agencyRole IS defined (e.g., 'agent'), trust it — isOwner may be true for a sub-agency, not the current agency
  else if (accountType !== 'enterprise' && isInAgency && !isOwnerOrAdmin) {
    if (agencyRole || agencyStatus?.isOwner !== true) {
      shouldBlock = true;
    }
  }

  // Handle redirect in useEffect to prevent rendering before redirect.
  // Shell-aware: this page is also embedded at /m/lead-assignment — a
  // hardcoded '/dashboard' would bounce mobile users onto the desktop app.
  const pathname = usePathname();
  useEffect(() => {
    if (shouldBlock) {
      router.push(pathname?.startsWith('/m') ? '/m/dashboard' : '/dashboard');
    }
  }, [shouldBlock, router, pathname]);

  // Show minimal loading only when redirect is needed (redirect happens via useEffect)
  if (shouldBlock) {
    return null;
  }

  return (
    <div className="space-y-8">

      {/* Header */}
      <div data-tour="assignment-header">
        <h1 className="text-3xl font-bold text-gray-900">Lead Assignment</h1>
        <p className="text-gray-600 mt-2">
          Assign leads to agents and track status progress
        </p>
      </div>

      {/* Summary Cards (hybrid) */}
      <SummaryCards stats={summaryStats} />

      {/* Tab Navigation */}
      <TabNavigation
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
          // Update URL without page reload
          const url = new URL(window.location.href);
          url.searchParams.set("tab", tab);
          window.history.pushState({}, "", url);

          // Refetch data when switching tabs to ensure fresh data
          if (tab === 'dashboard') {
            queryClient.invalidateQueries({ queryKey: ['unassignedLeads'] });
            queryClient.invalidateQueries({ queryKey: ['agentWorkload'] });
          } else if (tab === 'status') {
            queryClient.invalidateQueries({ queryKey: ['agentWorkload'] });
            queryClient.invalidateQueries({ queryKey: ['status-tracking'] });
          } else if (tab === 'activity') {
            // Both panels are plain state loaded on mount: re-read on open so
            // assignments made since then show, with the agent they went to.
            void loadActivityFeed();
            void loadActivity();
          }
        }}
        tabs={TAB_OPTIONS}
      />

      {/* Tab Content */}
      <div className="mt-6">
        {renderTabContent()}
      </div>

      {/* Auto-open Lead List modal */}
      <SelectImportListModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onAssignmentComplete={() => {
          // Deliberately does NOT close the modal. Owners assign a run of lists — one per
          // agent — and closing after each meant reopening, re-searching and re-scrolling
          // every time. The modal clears its own selection and refreshes its distribution
          // rows, so the list you just assigned visibly moves to its new holder, which is
          // the confirmation that it worked. Close is the X or Cancel.
          // refetchType 'all': the unassigned list's observer may be unmounted
          // behind this modal, and a default invalidation would only mark it stale.
          const all = { refetchType: 'all' as const };
          queryClient.invalidateQueries({ queryKey: ['unassignedLeads'], ...all });
          queryClient.invalidateQueries({ queryKey: ['agentWorkload'], ...all });
          queryClient.invalidateQueries({ queryKey: ['status-tracking'], ...all });
        }}
      />
    </div>
  );
};

export default LeadAssignment;
